import { hasValidCoordinates } from "@shared/helpers/geo";

export type PartnerOrgAddressDraft = {
  id?: string;
  clientId: string;
  label: string;
  address: string;
  coordinates: { lat: number; lng: number } | null;
};

export function emptyPartnerOrgAddressDraft(
  idFactory: () => string = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `addr-${Date.now()}-${Math.random()}`,
): PartnerOrgAddressDraft {
  return {
    clientId: idFactory(),
    label: "",
    address: "",
    coordinates: null,
  };
}

type PartnerOrgAddressRow = {
  id: string;
  sortOrder: number;
  label?: string | null;
  address?: string | null;
  coordinates?: { lat: number; lng: number } | null;
};

type PartnerOrgAddressSource = {
  address?: string | null;
  coordinates?: { lat: number; lng: number } | null;
  addresses?: PartnerOrgAddressRow[] | null;
};

export function draftsFromPartnerOrganization(
  organization: PartnerOrgAddressSource | null,
  idFactory?: () => string,
): PartnerOrgAddressDraft[] {
  const rows = [...(organization?.addresses ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );
  if (rows.length > 0) {
    return rows.map((row) => ({
      id: row.id,
      clientId: row.id,
      label: row.label ?? "",
      address: row.address ?? "",
      coordinates: hasValidCoordinates(row.coordinates)
        ? row.coordinates
        : null,
    }));
  }
  if (
    organization &&
    ((organization.address || "").trim() ||
      hasValidCoordinates(organization.coordinates))
  ) {
    return [
      {
        clientId: emptyPartnerOrgAddressDraft(idFactory).clientId,
        label: "",
        address: organization.address ?? "",
        coordinates: hasValidCoordinates(organization.coordinates)
          ? organization.coordinates
          : null,
      },
    ];
  }
  return [emptyPartnerOrgAddressDraft(idFactory)];
}

export function filledAddressDrafts(drafts: PartnerOrgAddressDraft[]) {
  return drafts.filter((draft) => draft.address.trim().length > 0);
}

export function primaryAddressFromDrafts(drafts: PartnerOrgAddressDraft[]): {
  address: string | undefined;
  coordinates: { lat: number; lng: number } | null;
} {
  const filled = filledAddressDrafts(drafts);
  const first = filled[0];
  if (!first) {
    return { address: undefined, coordinates: null };
  }
  return {
    address: first.address.trim(),
    coordinates: hasValidCoordinates(first.coordinates)
      ? first.coordinates
      : null,
  };
}
